import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

import { MODAL_ROOT_ID } from './modal-root'
import {
  MODAL_CLOSE_DELAY_MS,
  ModalComponent,
  ModalProps,
  ModalState,
} from './modal-component'
import { useAlertContext } from '../alert/context'

export const Modal: React.FC<ModalProps> = props => {
  const { id, isOpen = false, hasOverlay = true, onClose } = props
  const { closeAllAlerts } = useAlertContext()

  const modalRef = useRef<HTMLDivElement | null>(null)
  const [modalState, setModalState] = useState<ModalState>(
    isOpen ? ModalState.Open : ModalState.Closed
  )

  const setModalOpen = () => {
    const body = document.getElementsByTagName('body')[0]
    // close all alerts that might be open
    closeAllAlerts()

    if (hasOverlay) {
      // adds overflow hidden to the body, so that there aren't two scroll bars
      body.classList.add('modal-open')
    } else {
      // in case the previous modal had overlay,
      // we must remove overflow hidden from body
      body.classList.remove('modal-open')
    }
  }

  const setModalClosed = () => {
    // removes overflow hidden from the body
    const body = document.getElementsByTagName('body')[0]
    body.classList.remove('modal-open')
  }

  useEffect(() => {
    // create root element for the modal
    const modalElement = document.createElement('div')
    modalElement.setAttribute('id', id)

    // Append modal to the modal root in application root (ModalContextProvider adds)
    const modalRoot = document.getElementById(MODAL_ROOT_ID)

    if (!modalRoot) {
      // eslint-disable-next-line no-console
      console.error(
        'Modal root not found. Are you using the modal outside app.jsx?'
      )
    }

    modalRoot?.appendChild(modalElement)
    modalRef.current = modalElement

    // When modal unmounts, remove modal element from modal root
    return () => {
      if (isOpen) {
        setModalClosed()
      }
      modalRoot?.removeChild(modalElement)
    }
  }, [])

  // When modal isOpen boolean changes,
  // we change modal state to opening or closing
  // so that the opening and closing animation gets initiated
  useLayoutEffect(() => {
    if (isOpen) {
      setModalState(ModalState.Opening)
      setModalOpen()
    } else {
      setModalState(oldVal => {
        if (oldVal === ModalState.Closed) return oldVal
        return ModalState.Closing
      })
    }
  }, [isOpen])

  // After modalState has changed to opening or closing,
  // we must change the state from opening -> open or closing -> closed
  useEffect(() => {
    if (modalState === ModalState.Opening) {
      setModalState(ModalState.Open)
    } else if (modalState === ModalState.Closing) {
      setTimeout(() => {
        setModalState(ModalState.Closed)
        setModalClosed()
      }, MODAL_CLOSE_DELAY_MS)
    }
  }, [modalState])

  // If modal ref does not exist.
  // Probably because the Modal component is not inside ModalContextProvider
  if (!modalRef?.current) return null

  const isModalComponentVisible = [
    ModalState.Opening,
    ModalState.Open,
    ModalState.Closing,
  ].includes(modalState)

  return ReactDOM.createPortal(
    isModalComponentVisible ? (
      <ModalComponent state={modalState} {...props} onClose={onClose} />
    ) : null,
    modalRef.current
  )
}
